import React from 'react';
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { LayoutDashboard, MessageSquare, Users, User, Settings, LogOut, UserCog, Briefcase, DollarSign, Headphones, Download, Globe, Monitor, Bot } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { APP_VERSION } from '../../version';
import { itemMenuVisivel } from '../../utils/acessoAgente';


const DRAWER_WIDTH = 280;

interface NavItem {
    label: string;
    path: string;
    icon: React.ReactNode;
    adminOnly?: boolean; // Nova propriedade
    /** Visível também para representante (além de admin e supervisor). Não altera os demais itens. */
    representanteOk?: boolean;
}

const navItems: NavItem[] = [
    { label: 'Dashboard', path: '/', icon: <LayoutDashboard size={22} /> },
    { label: 'WhatsApp', path: '/whatsapp', icon: <MessageSquare size={22} /> },
    { label: 'Clientes', path: '/clientes', icon: <Users size={22} /> },
    { label: 'Atendimentos', path: '/atendimentos', icon: <Headphones size={22} /> },
    { label: 'Comissões', path: '/comissoes/representante', icon: <DollarSign size={22} /> },
    { label: 'Histórico Comissões', path: '/comissoes/historico', icon: <DollarSign size={22} /> },
    { label: 'Financeiro', path: '/comissoes/financeiro', icon: <DollarSign size={22} />, adminOnly: true },
    { label: 'Representantes', path: '/representantes', icon: <Briefcase size={22} />, adminOnly: true },
    { label: 'Usuários', path: '/usuarios', icon: <UserCog size={22} />, adminOnly: true },
    { label: 'Configurações', path: '/configuracoes', icon: <Settings size={22} />, adminOnly: true },
    { label: 'Domínios Cloudflare', path: '/cloudflare-domains', icon: <Globe size={22} />, adminOnly: true },
    { label: 'Downloads', path: '/downloads', icon: <Download size={22} /> },
    { label: 'Atualizador', path: '/updater-installations', icon: <Monitor size={22} /> },
    { label: 'Agente de Estação', path: '/agente', icon: <Bot size={22} />, representanteOk: true },
    { label: 'Perfil', path: '/perfil', icon: <User size={22} /> },
];

interface SidebarProps {
    mobileOpen: boolean;
    onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onClose }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { logout, user } = useAuth(); // Pegar user do contexto
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleNavigation = (path: string) => {
        navigate(path);
        if (isMobile) {
            onClose();
        }
    };

    // Filtrar itens baseado na role
    const filteredNavItems = navItems.filter(item => itemMenuVisivel(item, user));

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? mobileOpen : true}
            onClose={onClose}
            ModalProps={{
                keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                    backgroundColor: '#0D1117',
                    borderRight: '1px solid rgba(255, 255, 255, 0.05)',
                },
            }}
        >
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(45deg, #00D2FF 30%, #33DBFF 90%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        color: '#000'
                    }}
                >
                    N
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                        NEXOWhats
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
                        v{APP_VERSION}
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ opacity: 0.1 }} />

            <Box sx={{ overflow: 'auto', p: 2 }}>
                <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {filteredNavItems.map((item) => {
                        const active = location.pathname === item.path;
                        return (
                            <ListItem key={item.path} disablePadding>
                                <ListItemButton
                                    onClick={() => handleNavigation(item.path)}
                                    sx={{
                                        borderRadius: 2,
                                        backgroundColor: active ? 'rgba(0, 210, 255, 0.08)' : 'transparent',
                                        color: active ? '#00D2FF' : '#7D8590',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                            color: '#fff',
                                        },
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: 40,
                                        color: active ? '#00D2FF' : 'inherit'
                                    }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontWeight: active ? 700 : 500,
                                            fontSize: '0.95rem'
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </Box>

            <Box sx={{ mt: 'auto', p: 2 }}>
                <Divider sx={{ mb: 2, opacity: 0.1 }} />
                <ListItemButton
                    onClick={logout}
                    sx={{
                        borderRadius: 2,
                        color: '#f85149', // Red error color
                        '&:hover': {
                            backgroundColor: 'rgba(248, 81, 73, 0.1)',
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                        <LogOut size={22} />
                    </ListItemIcon>
                    <ListItemText primary="Sair da Conta" primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
